import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  useToast
} from '@chakra-ui/react';
import { FaExternalLinkAlt, FaHome } from 'react-icons/fa';
import { supabase } from '../supabaseClient';

export default function QRRedirect() {
  const { qrId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [qrData, setQrData] = useState(null);
  const [error, setError] = useState(null);
  const toast = useToast();

  useEffect(() => {
    if (qrId) {
      handleQRVisit();
    }
  }, [qrId]);

  const handleQRVisit = async () => {
    try {
      setLoading(true);

      // Get QR code data
      const { data: qrCode, error: qrError } = await supabase
        .from('qr_codes')
        .select('*')
        .eq('id', qrId)
        .single();

      if (qrError) {
        throw new Error('QR code not found');
      }

      if (!qrCode.is_active) {
        throw new Error('QR code is no longer active');
      }

      // Check expiration
      if (qrCode.expires_at && new Date(qrCode.expires_at) < new Date()) {
        throw new Error('QR code has expired');
      }

      // Check max uses
      if (qrCode.max_uses && qrCode.current_uses >= qrCode.max_uses) {
        throw new Error('QR code has reached maximum uses');
      }

      // Record the visit
      await recordVisit(qrCode.id);

      // Update usage count
      await supabase
        .from('qr_codes')
        .update({ current_uses: qrCode.current_uses + 1 })
        .eq('id', qrCode.id);

      setQrData(qrCode);

      // Redirect after a short delay
      setTimeout(() => {
        window.location.href = qrCode.redirect_url;
      }, 2000);

    } catch (err) {
      console.error('QR redirect error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const recordVisit = async (qrCodeId) => {
    try {
      // Get visitor information
      const visitorData = {
        qr_code_id: qrCodeId,
        ip_address: await getClientIP(),
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
        device_type: getDeviceType(),
        browser: getBrowser(),
        os: getOS()
      };

      const { error } = await supabase
        .from('qr_visits')
        .insert([visitorData]);

      if (error) {
        console.error('Error recording visit:', error);
      }
    } catch (err) {
      console.error('Error recording visit:', err);
    }
  };

  const getClientIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch {
      return null;
    }
  };

  const getDeviceType = () => {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  };

  const getBrowser = () => {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Other';
  };

  const getOS = () => {
    const ua = navigator.userAgent;
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iOS')) return 'iOS';
    return 'Other';
  };

  if (loading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text fontSize="lg" color="gray.600">
            Loading...
          </Text>
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" p={6}>
        <VStack spacing={6} maxW="md" textAlign="center">
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">QR Code Error</Text>
              <Text fontSize="sm">{error}</Text>
            </Box>
          </Alert>
          
          <Button
            leftIcon={<FaHome />}
            onClick={() => navigate('/')}
            colorScheme="blue"
          >
            Go Home
          </Button>
        </VStack>
      </Box>
    );
  }

  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" p={6}>
      <VStack spacing={6} maxW="md" textAlign="center">
        <VStack spacing={4}>
          <Text fontSize="2xl" fontWeight="bold" color="green.500">
            ✓ QR Code Valid
          </Text>
          <Text fontSize="lg" color="gray.600">
            {qrData?.name || 'Redirecting...'}
          </Text>
          {qrData?.description && (
            <Text fontSize="sm" color="gray.500">
              {qrData.description}
            </Text>
          )}
        </VStack>

        <VStack spacing={3}>
          <Text fontSize="sm" color="gray.600">
            Redirecting you to:
          </Text>
          <Box
            p={3}
            bg="gray.50"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
          >
            <Text fontSize="sm" color="blue.600" wordBreak="break-all">
              {qrData?.redirect_url}
            </Text>
          </Box>
        </VStack>

        <VStack spacing={4}>
          <Spinner size="md" color="blue.500" />
          <Text fontSize="sm" color="gray.500">
            Redirecting in a moment...
          </Text>
          
          <Button
            leftIcon={<FaExternalLinkAlt />}
            onClick={() => window.location.href = qrData?.redirect_url}
            colorScheme="blue"
            size="sm"
          >
            Go Now
          </Button>
        </VStack>

        <Text fontSize="xs" color="gray.400">
          Visit tracked for analytics
        </Text>
      </VStack>
    </Box>
  );
}
